'use client'
import React, { useState } from "react"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter, DialogClose } from "../../@/components/ui/dialog"
import { Button } from "../../@/components/ui/button"
import { Input } from "../../@/components/ui/input"
import { Form, FormField } from "@/components/ui/form"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../@/components/ui/select";
import { Textarea } from "../../@/components/ui/textarea"
import { Plus } from "lucide-react"
import { toast } from "sonner"
import axios from "axios"
import { api_endpoint } from "@/constants/constant"
import { ClipLoader } from "react-spinners";
import { useAuth } from "@clerk/clerk-react"
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "../../@/components/ui/field";
import { cn } from "@/lib/utils";

const addPathwaySchema = z.object({
  title: z.string().min(1, "This is required"),
  level: z.string().min(1, "This is required"),
  levelId: z
    .string()
    .min(1, "This is required")
    .transform((v) => Number(v))
    .refine((n) => Number.isFinite(n) && n > 0, "This is required"),
  courses: z
    .array(z.string())
    .min(1, "This is required")
    .transform((arr) => arr.map(Number)),
  description: z.string().min(1, "This is required"),
  // level: z.string().min(1, "Level is required")
})

export function AddPathwayDialog({ onAdd, refetchPathways }) {
  const [open, setOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [courses, setCourses] = useState([])
  const [isLoadingCourses, setIsLoadingCourses] = useState(false)
  const [pathwayTypes, setPathwayTypes] = useState([])
  const [isLoadingPathwayTypes, setIsLoadingPathwayTypes] = useState(false)
  const { getToken } = useAuth();

  const form = useForm({
    resolver: zodResolver(addPathwaySchema),
    defaultValues: {
      title: '',
      level: '',
      levelId: '',
      courses: [],
      description: '',
      // level: '',
    }
  })

  const handleCancel = () => {
    form.reset()
    setOpen(false)
  }



  const onSubmit = async (values) => {
    console.log("Form values:", values)
    try {
      setIsSubmitting(true)

      const payload = {
        title: values.title,
        description: values.description,
        level: values.level,
        level_id: values.levelId,
        courses: values.courses && values.courses.length > 0 ? values.courses : undefined,
      }

      const response = await axios.post(api_endpoint + "/pathway/create", payload)
      console.log('Pathway created:', response.data)
      toast.success(`Added pathway "${values.title}" successfully!`)
      if (onAdd) onAdd(response.data)
      if (refetchPathways) refetchPathways() // refetch pathways 
      form.reset()
      setOpen(false)
    } catch (error) {
      console.error("Failed to create pathway:", error)
      toast.error("Failed to create pathway. See console.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const fetchCourses = async () => {
    try {
      setIsLoadingCourses(true)

      const response = await axios.get(api_endpoint + "/course/")

      if (response.data && response.data.courses) {
        setCourses(response.data.courses)
      } else {
        setCourses([])
      }
    } catch (error) {
      setCourses([])
      toast.error('Failed to load courses')
    } finally {
      setIsLoadingCourses(false)
    }
  }

  const fetchPathwayTypes = async () => {
    try {
      setIsLoadingPathwayTypes(true)

      const token = await getToken();
      if (!token) throw new Error("No authentication token found")

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.get(api_endpoint + "/pathway/levels", reqConfig)
      const levels = response.data?.levels || []
      setPathwayTypes(levels)
    } catch (error) {
      setPathwayTypes([])
      toast.error('Failed to load pathway types')
    } finally {
      setIsLoadingPathwayTypes(false)
    }
  }

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (isOpen && courses.length === 0) {
      fetchCourses();
    }
    if (isOpen && pathwayTypes.length === 0) {
      fetchPathwayTypes();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="w-full md:w-auto" variant="">
          Add Pathway
          <Plus className="w-4 h-4" />
        </Button>
      </DialogTrigger>

      <DialogContent className="h-auto">
        <DialogHeader className="">
          <DialogTitle className="font-heading text-(--neutral-900)">Add New Pathway</DialogTitle>
          <DialogDescription className="font-body text-(--neutral-700)">Create a new pathway for your course.</DialogDescription>
        </DialogHeader>

        <div className="">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" id="add-pathway-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>Pathway Title *</FieldLabel>
                      <FieldContent>
                        <Input
                          {...field}
                          placeholder="Enter pathway title"
                          className={cn(
                            "border border-(--neutral-300) focus:ring-0 text-sm font-body",
                            fieldState.error &&
                              "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                          )}
                        />
                        <FieldError
                          errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                        />
                      </FieldContent>
                    </Field>
                  )}
                />

                <FormField
                  control={form.control}
                  name="levelId"           
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>Pathway Type *</FieldLabel>
                      <FieldContent>
                        <Select
                          onValueChange={(value) => {
                            field.onChange(value);
                            const selected = pathwayTypes.find((pt) => String(pt.id) === String(value));
                            form.setValue("level", selected?.name || "", { shouldValidate: true });
                          }}
                          value={field.value}
                        >
                          <SelectTrigger
                            className={cn(
                              "w-full",
                              fieldState.error &&
                                "border-(--color-destructive) focus:ring-(--color-primary)"
                            )}
                          >
                            <SelectValue
                              placeholder={isLoadingPathwayTypes ? "Loading pathway types..." : "Select pathway type"}
                              className="font-body"
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {pathwayTypes.map((pt) => (
                              <SelectItem key={pt.id ?? pt.name} value={String(pt.id)} className="font-body">
                                {pt.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FieldError
                          errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                        />
                      </FieldContent>
                    </Field>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="courses"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>Associated Courses *</FieldLabel>
                    <FieldContent>
                      <Select
                        onValueChange={(value) => {
                          if (value && !((field.value ?? []).includes(value))) {
                            field.onChange([...(field.value ?? []), value]);
                          }
                        }}
                        value=""
                        disabled={isLoadingCourses}
                      >
                        <SelectTrigger
                          className={cn(
                            "select-trigger mt-1 h-10 w-full font-body overflow-hidden [&_[data-slot=select-value]]:truncate",
                            fieldState.error &&
                              "border-(--color-destructive) focus:ring-(--color-primary)"
                          )}
                        >
                          <SelectValue placeholder={isLoadingCourses ? "Loading courses..." : "Select all that apply"} />
                        </SelectTrigger>
                        <SelectContent>
                          {courses.map((course) => (
                            <SelectItem key={course.id} value={String(course.id)} className="font-body">
                              {course.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    {(field.value ?? []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(field.value ?? []).map((courseId) => {
                          const course = courses.find(c => c.id === Number(courseId));
                          return (
                            <div
                              key={courseId}
                              className="tag-chip"
                            >
                              <span className="font-body">{course ? course.title : courseId}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  field.onChange((field.value ?? []).filter((id) => id !== courseId));
                                }}
                                className="ml-1 text-blue hover:text-blue/70"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                      <FieldError
                        errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                      />
                    </FieldContent>
                  </Field>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>Description *</FieldLabel>
                    <FieldContent>
                      <Textarea
                        {...field}
                        rows={4}
                        className={cn(
                          "min-h-[120px] resize-none border border-(--neutral-300) font-body",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                        )}
                        placeholder="Describe what this pathway covers..."
                      />
                      <FieldError
                        errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                      />
                    </FieldContent>
                  </Field>
                )}
              />

              {/* <FormField
                control={form.control}
                name="level"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Level *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="1, 2, 3" className="focus:border-red focus:ring-0 border-border-gray" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}
            </form>
          </Form>
        </div>

        <DialogFooter className="">
          <DialogClose asChild>
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleCancel} 
              disabled={isSubmitting} 
              className=""
              >
                Cancel
              </Button>
          </DialogClose>

          <Button
            type="submit"
            variant=""
            form="add-pathway-form"
            className=""
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center w-full">
                <ClipLoader size={20} color="#fff" />
              </span>
            ) : (
              "Add Pathway"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AddPathwayDialog
